import { useQuery } from "@apollo/client";
import { Tags, getUserTagValueOffEvent } from "@clockwise/client-commons/src/util/event-tag";
import {
  EventColorCategory,
  IEventWithAnnotatedEvents,
  useCalendarColors,
} from "@clockwise/web-commons/src/util/event-category-coloring";
import { getPreviousWeekday } from "@clockwise/web-commons/src/util/getPreviousWeekday";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { difference, isEmpty, isEqual, keys } from "lodash";
import { uniq } from "lodash/fp";
import { DateTime, DurationLike } from "luxon";
import React, { useCallback, useMemo } from "react";
import { useOrgId } from "../../hooks/useOrgId";
import { useUserProfile } from "../../hooks/useUserProfile";
import { CalendarEventsDocument } from "../apollo/__generated__/CalendarEvents.generated";
import { MultiCalEventsDocument } from "../apollo/__generated__/MultiCalEvents.generated";
import {
  CalendarEvent,
  MultiCalEvent,
  PlannerEventCard,
  PlannerEventCardsByDay,
  UseAICalendarEventCardsHook,
} from "../types";
import { parseAICalendarEventsDataGQL } from "../util/parseAICalendarEventsDataGQL";
import { parseMultiCalEventsDataGQL } from "../util/parseMultiCalEventsDataGQL";
import usePlannerMetaData from "./usePlannerMetaData";

export const useAICalendarEventCards: UseAICalendarEventCardsHook = (
  date: string,
  calendarIds: string[],
  ownCalendarIds: string[],
) => {
  const [weekStartDate, setWeekStartDate] = React.useState<string>("");
  const { getEventColors } = useCalendarColors();
  const { userProfile } = useUserProfile();
  const { colorSettings } = usePlannerMetaData();

  const eventColorGetter = useCallback(
    (event: IEventWithAnnotatedEvents) =>
      getEventColors(event, colorSettings, userProfile.userId, false, true),
    [colorSettings, getEventColors, userProfile.userId],
  );

  const eventCategoryGetter = useCallback(
    (event: IEventWithAnnotatedEvents): EventColorCategory | null | undefined =>
      userProfile.userId
        ? (getUserTagValueOffEvent(event, Tags.EventColoringCategory, userProfile.userId)
            ?.value as EventColorCategory)
        : null,
    [userProfile.userId],
  );

  const { orgId } = useOrgId();

  const timeZone = getRenderTimeZone();

  const newWeekStartDate = getPreviousWeekday(DateTime.fromISO(date), "sunday").toISODate();
  if (!isEqual(newWeekStartDate, weekStartDate)) {
    setWeekStartDate(newWeekStartDate);
  }

  const otherCalendarIds = useMemo(() => {
    return difference(calendarIds, ownCalendarIds);
  }, [calendarIds, ownCalendarIds]);

  const { data, loading, error, startPolling, stopPolling, refetch, fetchMore } = useQuery(
    CalendarEventsDocument,
    {
      skip: !orgId || !weekStartDate,
      variables: {
        orgId: orgId as string,
        weekStartDates: [weekStartDate],
        timeZone,
        calendarIds: ownCalendarIds,
        includeAllDay: true,
      },
      errorPolicy: "all", // This allows us to receive both error and data to able to load partial data.
      context: { useBatching: false },
    },
  );

  const [cardsByDay, events]: [PlannerEventCardsByDay, CalendarEvent[]] = useMemo(
    () =>
      data
        ? parseAICalendarEventsDataGQL(data, [weekStartDate], eventColorGetter, eventCategoryGetter)
        : [{}, []],
    [data, eventCategoryGetter, eventColorGetter, weekStartDate],
  );

  const { data: multiCalData, loading: loadingMultiCal, fetchMore: fetchMoreMultiCal } = useQuery(
    MultiCalEventsDocument,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        orgId: orgId!,
        weekStartDates: [weekStartDate],
        timeZone,
        calendarIds: otherCalendarIds,
        includeAllDay: true,
      },
      errorPolicy: "all", // This allows us to receive both error and data to able to load partial data.
      context: { useBatching: false },
      skip: otherCalendarIds.length === 0 || !orgId || !weekStartDate,
    },
  );

  const [multiCalCardsByDay]: [PlannerEventCardsByDay, MultiCalEvent[]] = useMemo(
    () =>
      multiCalData
        ? parseMultiCalEventsDataGQL(
            multiCalData,
            [weekStartDate],
            eventColorGetter,
            eventCategoryGetter,
          )
        : [{}, []],
    [eventCategoryGetter, eventColorGetter, multiCalData, weekStartDate],
  );

  const refetchAndstartPolling = useCallback(
    (time: number) => {
      void refetch();
      startPolling(time);
    },
    [refetch, startPolling],
  );

  const prefetch = useCallback(
    (relativeDuration: DurationLike) => {
      if (!orgId) {
        return;
      }

      const weekStartDates = [
        getPreviousWeekday(
          DateTime.fromISO(weekStartDate).plus(relativeDuration),
          "sunday",
        ).toISODate(),
      ];

      void fetchMore({
        variables: {
          weekStartDates,
        },
      });
      if (!isEmpty(otherCalendarIds)) {
        void fetchMoreMultiCal({
          variables: {
            weekStartDates,
          },
        });
      }
    },
    [fetchMore, fetchMoreMultiCal, orgId, otherCalendarIds, weekStartDate],
  );

  // @deprecated: maintaining this for backwards compatibility
  const getEventForCard = useCallback(
    (card: PlannerEventCard) => {
      return events.find((event) => event?.eventKey?.externalEventId === card.externalEventId);
    },
    [events],
  );

  const combinedCardsByDay = useMemo(() => {
    if (isEmpty(multiCalCardsByDay)) {
      return cardsByDay;
    } else {
      const combinedCardsByDay: PlannerEventCardsByDay = {};

      const dates = uniq([...keys(cardsByDay), ...keys(multiCalCardsByDay)]);
      dates.forEach((date) => {
        const ownerCardsForDay = cardsByDay[date] ?? [];
        const allOwnerExternalEventIds = ownerCardsForDay.map((card) => card.externalEventId);

        const multiCalCardsForDay = multiCalCardsByDay[date] ?? [];

        // The reason why we're removing all these duplicates is because
        // we're already showing the owner's event,
        // and we'll indicate the other user's event with a stripe on the side of the card.
        // with the color of the stripe being the color of the other user's calendar.
        const multiCalCardsForDayRemoveAllDuplicates = multiCalCardsForDay.filter(
          (card) => !allOwnerExternalEventIds.includes(card.externalEventId),
        );

        combinedCardsByDay[date] = ownerCardsForDay.concat(multiCalCardsForDayRemoveAllDuplicates);
      });

      return combinedCardsByDay;
    }
  }, [cardsByDay, multiCalCardsByDay]);

  return {
    cardsByDay: combinedCardsByDay,
    loading,
    loadingMultiCal: !!loadingMultiCal,
    error,
    getEventForCard,
    refetch: () => void refetch(),
    prefetch,
    startPolling: refetchAndstartPolling,
    stopPolling,
  };
};
